import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	Button,
	Dialog, DialogContent, DialogHeader,
	Input,
} from '@rentflowdev/ui'
import { useConnectPlatformMutation } from '@/api'

interface CianConnectDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
}

export function CianConnectDialog({ open, onOpenChange }: CianConnectDialogProps) {
	const { t } = useTranslation()
	const [connect, { isLoading, isError, reset }] = useConnectPlatformMutation()
	const [apiKey, setApiKey] = useState('')
	const [clientId, setClientId] = useState('')

	function handleClose() {
		reset()
		setApiKey('')
		setClientId('')
		onOpenChange(false)
	}

	async function handleSubmit() {
		if (!apiKey.trim() || !clientId.trim()) return
		try {
			await connect({
				platform: 'CIAN',
				credentialsJson: JSON.stringify({ apiKey: apiKey.trim(), clientId: clientId.trim() }),
			}).unwrap()
			handleClose()
		} catch {
			// isError surfaces in UI
		}
	}

	return (
		<Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : handleClose())} disablePointerDismissal>
			<DialogContent showCloseButton={false} className="sm:max-w-md">
				<DialogHeader title={t('settings.integrations.cianDialog.title')} />

				<div className="flex flex-col gap-4 px-6 pb-2">
					<label className="flex flex-col gap-1.5">
						<span className="text-sm font-[500] text-(--color-foreground)">
							{t('settings.integrations.cianDialog.apiKey')}
						</span>
						<Input
							type="password"
							value={apiKey}
							onChange={(e) => setApiKey(e.target.value)}
							autoComplete="off"
						/>
					</label>

					<label className="flex flex-col gap-1.5">
						<span className="text-sm font-[500] text-(--color-foreground)">
							{t('settings.integrations.cianDialog.clientId')}
						</span>
						<Input
							value={clientId}
							onChange={(e) => setClientId(e.target.value)}
							autoComplete="off"
						/>
					</label>

					{isError && (
						<p className="text-sm text-(--color-destructive)">
							{t('settings.integrations.cianDialog.errorTitle')}
						</p>
					)}
				</div>

				<div className="flex justify-end gap-2 border-t border-(--color-border) px-6 py-4">
					<Button
						type="button"
						disabled={isLoading || !apiKey.trim() || !clientId.trim()}
						onClick={() => void handleSubmit()}
					>
						{isLoading ? t('common.saving') : t('settings.integrations.cianDialog.submit')}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	)
}
